import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { ESignService } from '../../lib/api'
import type {
	CancelESignObjectParamsProps,
	CreateESignForVersionParamsProps,
	DeclinePublicESignObjectParamsProps,
	DeclineESignObjectParamsProps,
	DeleteESignObjectParamsProps,
	ESignLinkDataResponse,
	ESignLinkPreviewDataResponse,
	ESignObject,
	ESignStatus,
	GetESignLinkSignedContentParamsProps,
	GetESignLinkParamsProps,
	GetESignLinkPreviewParamsProps,
	GetESignObjectsDataResponse,
	GetESignObjectsForFileParamsProps,
	GetESignSignedContentParamsProps,
	SendESignObjectForSigningParamsProps,
	SetESignObjectContentParamsProps,
	RecordPublicESignConsentParamsProps,
	SignPublicESignObjectParamsProps,
	SignESignObjectParamsProps,
} from '../../lib/api/types/esign.interfaces'
import { getErrorData, handleApiError } from '../../lib/utils/handleApiError'
import toastCaller from '../../lib/utils/toastCaller'

const ESIGN_QUERY_KEY = 'eSign'
const ESIGN_LINK_QUERY_KEY = 'eSignLink'

const syncESignObjectCache = (
	queryClient: ReturnType<typeof useQueryClient>,
	eSignObjectId: string,
	data?: ESignObject,
) => {
	if (!data?.id) return
	if (String(data.id) !== String(eSignObjectId)) return

	queryClient.setQueryData([ESIGN_QUERY_KEY, 'object', eSignObjectId], data)
}

export const useESignObjects = (
	params?: {
		userId?: string
		fileId?: string
		versionId?: string
		status?: ESignStatus
		statuses?: ESignStatus[]
		limit?: number
		offset?: number
	},
	options?: Partial<QueryObserverOptions<GetESignObjectsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const { userId, fileId, versionId, status, statuses } = params ?? {}
	const resolvedStatus = status ?? statuses?.[0]

	return useQuery<GetESignObjectsDataResponse>({
		queryKey: [ESIGN_QUERY_KEY, 'objects', resolvedStatus ?? null],
		queryFn: () =>
			ESignService.getESignObjects({
				status,
				statuses,
			} as any),
		enabled: isAuthenticated,
		retry: false,
		...options,
	})
}

export const useESignObject = (
	eSignObjectId: string | null,
	options?: Partial<QueryObserverOptions<ESignObject>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<ESignObject>({
		queryKey: [ESIGN_QUERY_KEY, 'object', eSignObjectId],
		queryFn: () =>
			ESignService.getESignObject({
				eSignObjectId: eSignObjectId!,
			} as any),
		enabled: isAuthenticated && !!eSignObjectId,
		...options,
	})
}

export const useESignLink = (
	linkToken: string | null,
	options?: Partial<QueryObserverOptions<ESignLinkDataResponse>>,
) => {
	return useQuery<ESignLinkDataResponse>({
		queryKey: [ESIGN_LINK_QUERY_KEY, linkToken],
		queryFn: () =>
			ESignService.getESignLink({
				token: linkToken!,
			} as GetESignLinkParamsProps),
		enabled: !!linkToken,
		retry: false,
		...options,
	})
}

export const useESignLinkPreview = (
	linkToken: string | null,
	options?: Partial<QueryObserverOptions<ESignLinkPreviewDataResponse>>,
) => {
	return useQuery<ESignLinkPreviewDataResponse>({
		queryKey: [ESIGN_LINK_QUERY_KEY, 'preview', linkToken],
		queryFn: () =>
			ESignService.getESignLinkPreview({
				token: linkToken!,
			} as GetESignLinkPreviewParamsProps),
		enabled: !!linkToken,
		retry: false,
		...options,
	})
}

export const useESignSignedContentUrl = (
	eSignObjectId: string | null,
	options?: Partial<QueryObserverOptions<ESignLinkPreviewDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<ESignLinkPreviewDataResponse>({
		queryKey: [ESIGN_QUERY_KEY, 'signedContentUrl', eSignObjectId],
		queryFn: () =>
			ESignService.getESignSignedContent({
				eSignObjectId: eSignObjectId!,
			} as GetESignSignedContentParamsProps),
		enabled: isAuthenticated && !!eSignObjectId,
		refetchOnWindowFocus: false,
		retry: false,
		...options,
	})
}

const triggerSignedContentDownload = (url: string) => {
	const link = document.createElement('a')
	link.href = url
	link.rel = 'noopener noreferrer'
	link.download = ''
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

const showSignedContentLoadingToast = () => {
	toastCaller({
		update: true,
		type: 'info',
		message: 'Preparing signed document...',
		autoClose: 2000,
	})
}

const handleSignedContentSuccess = (data: ESignLinkPreviewDataResponse) => {
	const url = data?.data?.downloadUrl
	if (!url) {
		toastCaller({
			update: true,
			type: 'error',
			message: 'Download link is not available yet.',
			autoClose: 3000,
		})
		return
	}

	triggerSignedContentDownload(url)
	toastCaller({
		update: true,
		type: 'success',
		message: 'Download started in background.',
		autoClose: 3000,
	})
}

const handleSignedContentError = (error: unknown) => {
	const { errorMessageCode, errorMessage } = getErrorData(error)
	toastCaller({
		update: true,
		type: 'error',
		errorMessageCode,
		message: errorMessage ?? 'Failed to download signed document.',
		autoClose: 3000,
	})
}

export const useDownloadESignSignedContent = () =>
	useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'downloadSignedContent'],
		mutationFn: (args: GetESignSignedContentParamsProps) =>
			ESignService.getESignSignedContent(args),
		onMutate: () => {
			showSignedContentLoadingToast()
		},
		onError: (error) => handleSignedContentError(error),
		onSuccess: (data) => handleSignedContentSuccess(data),
	})

export const useDownloadESignLinkSignedContent = () =>
	useMutation({
		mutationKey: [ESIGN_LINK_QUERY_KEY, 'downloadSignedContent'],
		mutationFn: (args: GetESignLinkSignedContentParamsProps) =>
			ESignService.getESignLinkSignedContent(args),
		onMutate: () => {
			showSignedContentLoadingToast()
		},
		onError: (error) => handleSignedContentError(error),
		onSuccess: (data) => handleSignedContentSuccess(data),
	})

export const useCreateESignForVersion = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'createForVersion'],
		mutationFn: (args: CreateESignForVersionParamsProps) =>
			ESignService.createESignForVersion(args),
		onError: (error, args) =>
			handleApiError(error, `${args.fileId}/${args.versionId}`),
		onSuccess: (data, args) => {
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'objects'],
				exact: false,
			})
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'fileObjects', args.fileId],
				exact: false,
			})

			if (data?.eSign?.id) {
				queryClient.invalidateQueries({
					queryKey: [ESIGN_QUERY_KEY, 'object', data.eSign.id],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useESignObjectsForFile = (
	params: {
		fileId: string
		status?: string
		onlyMyRecipients?: boolean
	},
	options?: Partial<QueryObserverOptions<GetESignObjectsDataResponse>>,
) => {
	const { isAuthenticated } = useAuth()
	const { fileId, status, onlyMyRecipients } = params

	return useQuery<GetESignObjectsDataResponse>({
		queryKey: [
			ESIGN_QUERY_KEY,
			'fileObjects',
			fileId,
			status ?? null,
			onlyMyRecipients ?? null,
		],
		queryFn: () =>
			ESignService.getESignObjectsForFile({
				fileId,
				status,
				onlyMyRecipients,
			} as GetESignObjectsForFileParamsProps),
		enabled: isAuthenticated && !!fileId,
		...options,
	})
}

export const useSaveESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'saveObject'],
		mutationFn: (args: SetESignObjectContentParamsProps) =>
			ESignService.setESignObjectContent(args),
		onError: (error, args) => handleApiError(error, args.eSignObjectId),
		onSuccess: (data, args) => {
			if (!args?.withoutToast) {
				toastCaller({
					type: 'success',
					message: 'Signing document saved successfully.',
				})
			}
			syncESignObjectCache(queryClient, args.eSignObjectId, data)
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'objects'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useSendESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'sendObject'],
		mutationFn: (args: SendESignObjectForSigningParamsProps) =>
			ESignService.sendESignObjectForSigning(args),
		onError: (error, args) => handleApiError(error, String(args.eSignObjectId)),
		onSuccess: (data, args) => {
			if (!args?.withoutToast) {
				toastCaller({
					type: 'success',
					message: 'Document sent to sign successfully!',
				})
			}
			// queryClient.invalidateQueries({
			// 	queryKey: [ESIGN_QUERY_KEY, 'object', String(args.eSignObjectId)],
			// })
			// queryClient.invalidateQueries({
			// 	queryKey: [ESIGN_QUERY_KEY, 'fileObjects'],
			// 	exact: false,
			// })
			// queryClient.invalidateQueries({
			// 	queryKey: [ESIGN_QUERY_KEY, 'objects'],
			// 	exact: false,
			// })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useCancelESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'cancelObject'],
		mutationFn: (args: CancelESignObjectParamsProps) =>
			ESignService.cancelESignObject(args),
		onError: (error, args) =>
			handleApiError(error, String(args?.eSignObjectId)),
		onSuccess: (_, args) => {
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'object', String(args.eSignObjectId)],
			})
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'fileObjects'],
				exact: false,
			})
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'objects'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeleteESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'deleteObject'],
		mutationFn: (args: DeleteESignObjectParamsProps) =>
			ESignService.deleteESignObject(args),
		onError: (error, args) =>
			handleApiError(error, String(args?.eSignObjectId)),
		onSuccess: (_, args) => {
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'object', args.eSignObjectId],
			})
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'fileObjects'],
				exact: false,
			})
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'objects'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useRecordPublicESignConsent = () => {
	const mutation = useMutation({
		mutationKey: [ESIGN_LINK_QUERY_KEY, 'electronic-consent'],
		mutationFn: (args: RecordPublicESignConsentParamsProps) =>
			ESignService.recordPublicESignConsent(args),
		onError: (error, args) => handleApiError(error, String(args.eSignObjectId)),
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useSignPublicESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_LINK_QUERY_KEY, 'sign'],
		mutationFn: (args: SignPublicESignObjectParamsProps) =>
			ESignService.signPublicESignObject(args),
		onError: (error, args) => handleApiError(error, String(args.eSignObjectId)),
		onSuccess: (_, args) => {
			queryClient.invalidateQueries({
				queryKey: [ESIGN_LINK_QUERY_KEY, args.token],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useSignESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'sign'],
		mutationFn: (args: SignESignObjectParamsProps) =>
			ESignService.signESignObject(args),
		onError: (error, args) => handleApiError(error, String(args.eSignObjectId)),
		onSuccess: (data, args) => {
			syncESignObjectCache(queryClient, String(args.eSignObjectId), data)
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'objects'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeclinePublicESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_LINK_QUERY_KEY, 'decline'],
		mutationFn: (args: DeclinePublicESignObjectParamsProps) =>
			ESignService.declinePublicESignObject(args),
		onError: (error, args) => handleApiError(error, String(args.eSignObjectId)),
		onSuccess: (_, args) => {
			queryClient.invalidateQueries({
				queryKey: [ESIGN_LINK_QUERY_KEY, args.token],
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}

export const useDeclineESignObject = () => {
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationKey: [ESIGN_QUERY_KEY, 'decline'],
		mutationFn: (args: DeclineESignObjectParamsProps) =>
			ESignService.declineESignObject(args),
		onError: (error, args) => handleApiError(error, String(args.eSignObjectId)),
		onSuccess: (data, args) => {
			syncESignObjectCache(queryClient, String(args.eSignObjectId), data)
			queryClient.invalidateQueries({
				queryKey: [ESIGN_QUERY_KEY, 'objects'],
				exact: false,
			})
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			return await mutation.mutateAsync(...params)
		} catch (error) {
			console.error(error)
		}
	}

	return { ...mutation, mutateAsync }
}
